import type { BacklightReport } from '@/core/types';
import { requireShape } from '@/core/shapes/registry';
import { distanceToOutline } from '@/core/heightfield';
import { CONSTRAINTS, QUALITY_PRESETS, suggestedSegments } from '@/core/design/constraints';
import { useDesignStore } from '@/state/store';
import { Section, Segmented, Slider, Toggle } from '../components/Controls';
import { ShapePicker } from '../components/ShapePicker';
import { LedSection } from './LedSection';

/**
 * Step 2 — the physical panel.
 *
 * Panel size is the headline control and is bounded to 50–250 mm by the same
 * constraint table the validator uses, so a design can never reach the mesh
 * builder outside that range.
 */

const EDGE_MODES = [
  { value: 'seamless', label: 'Seamless', help: 'The relief continues across every joint.' },
  { value: 'flat-border', label: 'Framed', help: 'A flat margin frames each panel.' },
  {
    value: 'chamfer',
    label: 'Chamfered',
    help: 'The relief ramps down into a V-groove at the joint.',
  },
] as const;

type EdgeMode = (typeof EDGE_MODES)[number]['value'];

export function PanelStep({ backlight }: { backlight: BacklightReport | null }) {
  const design = useDesignStore((state) => state.design);
  const setPanelSpec = useDesignStore((state) => state.setPanelSpec);
  const setGrid = useDesignStore((state) => state.setGrid);
  const setFinish = useDesignStore((state) => state.setFinish);

  const { panel, grid, finish } = design;
  const recommended = suggestedSegments(panel.size);

  // The border can only eat into the panel as far as its inradius — which is
  // exactly the distance from the centre to the nearest edge, and much smaller
  // than half the width for a triangle.
  const shape = requireShape(panel.shapeId);
  const inradius = distanceToOutline(0, 0, shape.outline(panel.size, panel.gap));
  const maxEdgeWidth = Math.min(CONSTRAINTS.edgeWidth.max, Math.max(1, inradius * 0.7));

  // Only the shape knows how its panels tile, so the wall size comes from it
  // rather than from square arithmetic that happens to be right for one shape.
  const metrics = shape.metrics(grid, panel.size, panel.gap);

  return (
    <div className="step">
      <header className="step__header">
        <h2 className="step__title">Panel</h2>
        <p className="step__subtitle">
          {metrics.width.toFixed(0)} × {metrics.height.toFixed(0)} mm wall ·{' '}
          {(panel.baseThickness + panel.reliefDepth).toFixed(1)} mm thick
        </p>
      </header>

      <div className="step__body">
        <Section title="Size and layout">
          <ShapePicker value={panel.shapeId} onChange={(shapeId) => setPanelSpec({ shapeId })} />

          <Slider
            label="Panel size"
            value={panel.size}
            min={CONSTRAINTS.panelSize.min}
            max={CONSTRAINTS.panelSize.max}
            step={CONSTRAINTS.panelSize.step}
            unit="mm"
            help="Motifs measured in panels rescale with this, so the design keeps meeting the joints."
            onChange={(size) => setPanelSpec({ size })}
          />
          <Slider
            label="Columns"
            value={grid.columns}
            min={CONSTRAINTS.columns.min}
            max={CONSTRAINTS.columns.max}
            step={1}
            onChange={(columns) => setGrid({ columns })}
          />
          <Slider
            label="Rows"
            value={grid.rows}
            min={CONSTRAINTS.rows.min}
            max={CONSTRAINTS.rows.max}
            step={1}
            onChange={(rows) => setGrid({ rows })}
          />
          <Slider
            label="Mounting gap"
            value={panel.gap}
            min={CONSTRAINTS.gap.min}
            max={CONSTRAINTS.gap.max}
            step={CONSTRAINTS.gap.step}
            unit="mm"
            help={
              panel.shapeId === 'triangle'
                ? 'Triangles realise the gap by shrinking the panel, because spreading their lattice would open uneven joints.'
                : 'Physical spacing between panels. The pattern accounts for it, so the motif still lines up.'
            }
            onChange={(gap) => setPanelSpec({ gap })}
          />
        </Section>

        <Section title="Relief">
          <Slider
            label="Relief depth"
            value={panel.reliefDepth}
            min={CONSTRAINTS.reliefDepth.min}
            max={CONSTRAINTS.reliefDepth.max}
            step={CONSTRAINTS.reliefDepth.step}
            unit="mm"
            help="How far the pattern rises above the backing plate."
            onChange={(reliefDepth) => setPanelSpec({ reliefDepth })}
          />
          <Slider
            label="Backing plate"
            value={panel.baseThickness}
            min={CONSTRAINTS.baseThickness.min}
            max={CONSTRAINTS.baseThickness.max}
            step={CONSTRAINTS.baseThickness.step}
            unit="mm"
            help="Solid material under the relief. Keep it at two or more perimeters for a rigid panel."
            onChange={(baseThickness) => setPanelSpec({ baseThickness })}
          />
        </Section>

        <Section title="Edges" description="What happens where two panels meet.">
          <Segmented<EdgeMode>
            value={panel.edge.mode}
            options={EDGE_MODES}
            onChange={(mode) => setPanelSpec({ edge: { ...panel.edge, mode } })}
          />
          {panel.edge.mode !== 'seamless' ? (
            <Slider
              label="Border width"
              value={Math.min(panel.edge.width, maxEdgeWidth)}
              min={CONSTRAINTS.edgeWidth.min}
              max={maxEdgeWidth}
              step={CONSTRAINTS.edgeWidth.step}
              unit="mm"
              help={`Capped at ${maxEdgeWidth.toFixed(0)} mm for this shape and size.`}
              onChange={(width) => setPanelSpec({ edge: { ...panel.edge, width } })}
            />
          ) : null}
        </Section>

        <LedSection report={backlight} />

        <Section
          title="Finish"
          description="Shapes the whole relief after the layers have been combined."
        >
          <Toggle
            label="Use the full depth"
            checked={finish.autoLevel}
            help="Rescales the relief so its lowest point sits on the plate and its highest reaches the full depth."
            onChange={(autoLevel) => setFinish({ autoLevel })}
          />
          <Slider
            label="Gamma"
            value={finish.gamma}
            min={CONSTRAINTS.gamma.min}
            max={CONSTRAINTS.gamma.max}
            step={CONSTRAINTS.gamma.step}
            help="Below 1 lifts the low areas; above 1 deepens the valleys."
            onChange={(gamma) => setFinish({ gamma })}
          />
          <Slider
            label="Contrast"
            value={finish.contrast}
            min={CONSTRAINTS.contrast.min}
            max={CONSTRAINTS.contrast.max}
            step={CONSTRAINTS.contrast.step}
            help="Pushes peaks and valleys apart, sharpening the transition between them."
            onChange={(contrast) => setFinish({ contrast })}
          />
          <Slider
            label="Terracing"
            value={finish.terraceSteps}
            min={CONSTRAINTS.terraceSteps.min}
            max={CONSTRAINTS.terraceSteps.max}
            step={1}
            help="Snaps the relief into discrete levels. Zero keeps it continuous."
            onChange={(terraceSteps) => setFinish({ terraceSteps })}
          />
          <Toggle
            label="Invert"
            checked={finish.invert}
            help="Turns peaks into valleys."
            onChange={(invert) => setFinish({ invert })}
          />
        </Section>

        <Section
          title="Mesh resolution"
          description="Used for the export. The preview may run coarser to stay responsive."
        >
          <Segmented
            value={String(panel.segments)}
            options={QUALITY_PRESETS.map((preset) => ({
              value: String(preset.segments),
              label: preset.label,
              help: preset.help,
            }))}
            onChange={(segments) => setPanelSpec({ segments: Number(segments) })}
          />
          <Slider
            label="Segments per edge"
            value={panel.segments}
            min={CONSTRAINTS.segments.min}
            max={CONSTRAINTS.segments.max}
            step={CONSTRAINTS.segments.step}
            help={`About ${(panel.size / panel.segments).toFixed(2)} mm between samples. ${
              panel.segments < recommended
                ? `A ${panel.size} mm panel resolves fine detail at around ${recommended}.`
                : 'Fine enough for detailed patterns at this size.'
            }`}
            onChange={(segments) => setPanelSpec({ segments })}
          />
        </Section>
      </div>
    </div>
  );
}
